import { useApolloClient, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import {
  FullscreenPanelContainer,
  FullscreenPanelLeft,
  FullscreenPanelRight,
} from '~/components/FullscreenPanel';
import { LoadingPane } from '~/components/loading-pane';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { MediaItemViewDocument } from './MediaItemView.generated';
import { MediaItemPreview, MediaItemToolbar } from './components';

export const MediaItemView = () => {
  const { t } = useTranslation();
  const params = useParams<{ mediaItemId: string }>();
  const mediaItemId = parseInt(params.mediaItemId ?? '');
  const { currentNetwork } = useAppContext();
  const link = useLink();
  const client = useApolloClient();
  const currentFileId = useRef<number>();
  const sawNonReady = useRef(false);

  const { data, loading, startPolling, stopPolling } = useQuery(MediaItemViewDocument, {
    variables: { id: mediaItemId, networkId: currentNetwork.id },
  });

  useEffect(() => {
    const fileId = data?.network?.mediaItem?.file?.id;
    if (!fileId || currentFileId.current === fileId) return;

    if (currentFileId.current) {
      void client.refetchQueries({
        include: ['MediaItemDetails', 'MediaItemThumbnails', 'MediaItemVersions'],
      });
    }
    currentFileId.current = fileId;
  }, [client, data]);

  useEffect(() => {
    const status = data?.network?.mediaItem?.status;
    if (!status) return;
    if (status === 'READY') {
      if (sawNonReady.current) {
        sawNonReady.current = false;
        stopPolling();
      }
    } else {
      sawNonReady.current = true;
      startPolling(1_000);
    }
  }, [data, startPolling, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleOnReplace = useCallback(() => {
    startPolling(1_000);
  }, [startPolling]);

  if (!loading && !data?.network?.mediaItem) return <Navigate to={link('/not-found')} replace />;

  const mediaItem = data?.network?.mediaItem;

  return (
    <LoadingPane in={loading && !data}>
      {mediaItem && (
        <>
          <MediaItemToolbar mediaItem={mediaItem} onReplace={handleOnReplace} />
          <FullscreenPanelContainer>
            <FullscreenPanelLeft
              tabs={[
                { label: t('common.labels.details'), to: 'details' },
                { label: t('pages.content.media_details.tabs.renditions'), to: 'renditions' },
                { label: t('pages.content.media_details.tabs.thumbnails'), to: 'thumbnails' },
                { label: t('pages.content.media_details.tabs.usage'), to: 'usage' },
                ...(currentNetwork.canManage.value
                  ? [
                      {
                        label: t('common.tabs.audit_logs'),
                        to: 'audit-logs',
                      },
                    ]
                  : []),
              ]}
            >
              <Outlet />
            </FullscreenPanelLeft>
            <FullscreenPanelRight>
              <MediaItemPreview mediaItem={mediaItem} />
            </FullscreenPanelRight>
          </FullscreenPanelContainer>
        </>
      )}
    </LoadingPane>
  );
};
